import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const COLORS = {
  bg: '#0f172a',
  panel: '#1e293b',
  border: '#334155',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  accent: '#4ecca3',
  peak: '#dc2626',
  standard: '#d97706',
  offPeak: '#059669',
};

const PERIOD_OPTIONS = [
  { value: 'off_peak', label: 'Off-peak' },
  { value: 'standard', label: 'Standard' },
  { value: 'peak', label: 'Peak' },
];

const DAY_TYPES = [
  { key: 'weekday', label: 'Weekday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const periodColor = (p) => {
  if (p === 'peak') return COLORS.peak;
  if (p === 'standard') return COLORS.standard;
  return COLORS.offPeak;
};

const TOUScheduleEditor = () => {
  const [schedules, setSchedules] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load list of schedules once
  useEffect(() => {
    fetch(`${API}/api/tou-schedules?country=ZA`)
      .then((r) => r.json())
      .then((list) => {
        setSchedules(list || []);
        if (list && list.length > 0) {
          setSelectedId(list[0].id);
        }
      })
      .catch((e) => setError('Failed to load schedules: ' + e.message));
  }, []);

  // Load detail when selection changes
  const loadDetail = useCallback((id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/api/tou-schedules/${id}`)
      .then((r) => r.json())
      .then((d) => {
        setSchedule(d.schedule);
        setMatrix(d.matrix);
        setDirty(false);
      })
      .catch((e) => setError('Failed to load schedule: ' + e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleCellChange = (dayKey, hour, value) => {
    setMatrix((prev) => {
      const row = [...prev[dayKey]];
      row[hour] = value;
      return { ...prev, [dayKey]: row };
    });
    setDirty(true);
  };

  const handleSave = () => {
    if (!selectedId || !matrix) return;
    setSaving(true);
    setError(null);
    fetch(`${API}/api/tou-schedules/${selectedId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ matrix }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(() => loadDetail(selectedId))
      .catch((e) => setError('Save failed: ' + e.message))
      .finally(() => setSaving(false));
  };

  const handleDiscard = () => {
    loadDetail(selectedId);
  };

  return (
    <div style={{ color: COLORS.text, fontFamily: "'Work Sans', sans-serif" }}>
      {/* Schedule picker + header */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: COLORS.textDim,
          marginBottom: '6px',
        }}>
          Schedule
        </label>
        <select
          value={selectedId || ''}
          onChange={(e) => setSelectedId(Number(e.target.value))}
          style={{
            background: COLORS.panel,
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '4px',
            padding: '8px 12px',
            fontSize: '14px',
            minWidth: '320px',
            fontFamily: 'inherit',
          }}
        >
          {schedules.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        {schedule && (
          <div style={{
            marginTop: '10px',
            fontSize: '13px',
            color: COLORS.textDim,
            maxWidth: '900px',
            lineHeight: 1.5,
          }}>
            {schedule.description}
            {schedule.source_document && (
              <div style={{ marginTop: '4px', fontSize: '12px', fontStyle: 'italic' }}>
                Source: {schedule.source_document}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px',
          background: '#7f1d1d',
          color: '#fecaca',
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {loading && <div style={{ color: COLORS.textDim }}>Loading…</div>}

      {matrix && !loading && (
        <>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '12px' }}>
            {PERIOD_OPTIONS.map((opt) => (
              <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '14px',
                  height: '14px',
                  background: periodColor(opt.value),
                  borderRadius: '2px',
                }} />
                <span style={{ color: COLORS.textDim }}>{opt.label}</span>
              </div>
            ))}
          </div>

          {/* Grid */}
          <div style={{
            background: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '6px',
            padding: '12px',
            overflowX: 'auto',
          }}>
            <table style={{
              borderCollapse: 'separate',
              borderSpacing: '2px',
              fontFamily: "'JetBrains Mono', monospace",
              width: '100%',
              tableLayout: 'fixed',
              minWidth: '640px',
            }}>
              <colgroup>
                <col style={{ width: '70px' }} />
                {HOURS.map((h) => (
                  <col key={h} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  {HOURS.map((h) => (
                    <th key={h} style={{
                      fontSize: 'clamp(9px, 0.85vw, 12px)',
                      color: COLORS.textDim,
                      fontWeight: 400,
                      padding: '4px 1px',
                      textAlign: 'center',
                    }}>
                      {String(h).padStart(2, '0')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DAY_TYPES.map(({ key, label }) => (
                  <tr key={key}>
                    <td style={{
                      fontSize: 'clamp(11px, 0.9vw, 13px)',
                      color: COLORS.text,
                      fontWeight: 600,
                      padding: '6px 8px',
                      textAlign: 'right',
                      fontFamily: "'Work Sans', sans-serif",
                      whiteSpace: 'nowrap',
                    }}>
                      {label}
                    </td>
                    {HOURS.map((h) => {
                      const value = matrix[key][h];
                      return (
                        <td key={h} style={{ padding: 0 }}>
                          <select
                            value={value}
                            onChange={(e) => handleCellChange(key, h, e.target.value)}
                            style={{
                              background: periodColor(value),
                              color: '#ffffff',
                              border: 'none',
                              borderRadius: '3px',
                              padding: '6px 1px',
                              fontSize: 'clamp(9px, 0.8vw, 11px)',
                              width: '100%',
                              minWidth: '24px',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              textAlign: 'center',
                              textAlignLast: 'center',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              MozAppearance: 'none',
                            }}
                          >
                            {PERIOD_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value} style={{ background: COLORS.panel, color: COLORS.text }}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save bar */}
          <div style={{
            marginTop: '16px',
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
          }}>
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                background: dirty ? COLORS.accent : COLORS.border,
                color: dirty ? '#0f172a' : COLORS.textDim,
                border: 'none',
                borderRadius: '4px',
                padding: '10px 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {dirty && (
              <button
                onClick={handleDiscard}
                disabled={saving}
                style={{
                  background: 'transparent',
                  color: COLORS.textDim,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '4px',
                  padding: '10px 18px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Discard
              </button>
            )}
            {dirty && (
              <span style={{ fontSize: '12px', color: COLORS.textDim }}>
                Unsaved changes
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TOUScheduleEditor;
